// @ts-check
// Taylor's Boats — Content banner carousel.
// Fade-cross slides driven by `is-active`. Prev/next arrows, dots, and a
// "n / total" counter. Autoplay opt-in via data-autoplay; pauses on hover
// and after any user interaction. Pairs with sections/content-banner-carousel.liquid.

class ContentBannerCarousel extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.autoplayTimer = null;
    /** @type {{ x: number, y: number } | null} */
    this.swipeStart = null;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    this.counter = this.querySelector('[data-cbc-counter]');
    this.prevBtn = this.querySelector('[data-cbc-prev]');
    this.nextBtn = this.querySelector('[data-cbc-next]');

    if (this.slides.length <= 1) return;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(/** @type {HTMLElement} */ (dot).dataset.dotIndex || '0', 10);
        this.setActive(idx);
        this.stopAutoplay();
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.setActive(this.activeIndex - 1);
        this.stopAutoplay();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.setActive(this.activeIndex + 1);
        this.stopAutoplay();
      });
    }

    const viewport = this.querySelector('.cbc__viewport');
    if (viewport) {
      // Swipe support — horizontal pointer gestures advance the carousel.
      // touch-action: pan-y on .cbc__viewport keeps vertical page scroll native.
      const SWIPE_THRESHOLD = 40;
      viewport.addEventListener('pointerdown', (event) => {
        this.swipeStart = { x: event.clientX, y: event.clientY };
      });
      viewport.addEventListener('pointerup', (event) => {
        if (!this.swipeStart) return;
        const dx = event.clientX - this.swipeStart.x;
        const dy = event.clientY - this.swipeStart.y;
        this.swipeStart = null;
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
        this.setActive(this.activeIndex + (dx < 0 ? 1 : -1));
        this.stopAutoplay();
      });
      viewport.addEventListener('pointercancel', () => {
        this.swipeStart = null;
      });
      // Without this, dragging the background image starts a native drag and
      // the pointerup that would complete the swipe never fires.
      viewport.addEventListener('dragstart', (event) => event.preventDefault());
    }

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
        this.stopAutoplay();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.setActive(this.activeIndex + 1);
        this.stopAutoplay();
      }
    });

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = this.dataset.autoplay === 'true' && !prefersReducedMotion;
    const interval = parseInt(this.dataset.interval || '6000', 10);

    if (autoplayEnabled) {
      this.startAutoplay(interval);
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay(interval));
      this.addEventListener('focusin', () => this.stopAutoplay());
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  /** @param {number} index */
  setActive(index) {
    const total = this.slides.length;
    if (total === 0) return;

    const next = ((index % total) + total) % total;
    this.activeIndex = next;

    this.slides.forEach((slide, i) => {
      const isActive = i === next;
      slide.classList.toggle('is-active', isActive);
      // `inert` removes inactive slides (and their CTAs) from both tab order
      // and the AT tree, which `pointer-events: none` alone does not do.
      // `inert` implies hidden-from-AT, so we drop the redundant aria-hidden.
      /** @type {HTMLElement} */ (slide).inert = !isActive;
    });
    this.dots.forEach((dot, i) => {
      const isActive = i === next;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    if (this.counter) {
      this.counter.textContent = String(next + 1);
    }
  }

  /** @param {number} interval */
  startAutoplay(interval) {
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => {
      this.setActive(this.activeIndex + 1);
    }, interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('content-banner-carousel')) {
  customElements.define('content-banner-carousel', ContentBannerCarousel);
}
